import { ReactNode } from 'react';
import useResponsive from '@creditflowresearch/uilib/hooks/useResponsive';
import { PANEL_TIMINGS } from '@creditflowresearch/uilib/PanelControl/timings';
import MobileDropdownPanelButtons from './MobileDropdownPanelButtons';
import DesktopDropdownPanelButtons from './DesktopDropdownPanelButtons';

export interface INavItem {
  id: string,
  label: string,
  abbreviation: string,
  badge: ReactNode | ((isOpen: boolean) => ReactNode),
  href?: string,
}

interface IDropdownPanelButtonsProps {
  items: INavItem[],
  getPanelContent?: (panelId: string) => ReactNode,
  className?: string,
  /** Delay before opening on hover (ms). Defaults to `PANEL_TIMINGS.openDelay` */
  delayDuration?: number,
  /** Skip delay window for fast switching (ms). Defaults to `PANEL_TIMINGS.skipDelay` */
  skipDelayDuration?: number,
  /** Delay before closing on mouse leave (ms). Defaults to `PANEL_TIMINGS.leaveDelay` */
  leaveDelay?: number,
}

/**
 * Desktop: Shared viewport pattern — a single panel stays open while switching
 * between items. Content swaps instantly, panel only animates on first open / final close.
 *
 * Mobile: Each item uses TriggerControl with click-to-toggle.
 */
const DropdownPanelButtons = ({
  items,
  getPanelContent,
  className,
  delayDuration = PANEL_TIMINGS.openDelay,
  skipDelayDuration = PANEL_TIMINGS.skipDelay,
  leaveDelay = PANEL_TIMINGS.leaveDelay,
}: IDropdownPanelButtonsProps) => {
  const isMobile = useResponsive(({ width }) => width < 1024);

  if (!getPanelContent) {
    return null;
  }

  if (isMobile) {
    return (
      <MobileDropdownPanelButtons
        items={ items }
        getPanelContent={ getPanelContent }
        className={ className }
      />
    );
  }

  return (
    <DesktopDropdownPanelButtons
      items={ items }
      getPanelContent={ getPanelContent }
      className={ className }
      delayDuration={ delayDuration }
      skipDelayDuration={ skipDelayDuration }
      leaveDelay={ leaveDelay }
    />
  );
};

export default DropdownPanelButtons;
